// npm modules
import keyBy from 'lodash/keyBy';
import moment from 'moment';
// api modules
import {
  availableDeliverySlotsRequest,
  availableCOMApplianceDeliverySlotRequest,
  applianceRescheduleRequest,
  comApplianceRescheduleRequest,
  applianceCancelEligibilityRequest,
  applianceCancelRequest,
} from '../api/requestCreator';
import { postRequest } from '../api';
// common actions
import { resetError } from './common';
// reducers
import {
  getOrderLineItems,
  getOrderRefNumber,
  getActionData,
  getMappedData,
} from '../reducers';
import { getOrderDetails } from './orderDetails';
// constants
import { ROUTES, ACTIONS, REDUCERS, ERRORS } from '../constants';
// utils
import { isEligibleApplianceItem } from '../utils/validations';
import { getErrorCode } from '../utils/errors';
import { scrollToTop } from '../utils/window';
// analytics
import {
  setApplianceDeliveryRescheduleInfo,
  setADRSuccessOverlayInfo,
  applianceDeliveryRescheduleError,
  setChangeDeliveryDateOverlayInfo,
  errorCalendarResponse,
  setApplianceCancelPageInfo,
  setApplianceCancelSuccess,
  setApplianceCancelErrorEvent,
} from '../analytics/gatherService';

/**
 * APPLIANCE RESCHEDULE ACTIONS
 */

export const resetApplianceRescheduleActionData = () => ({
  type: ACTIONS.RESET_APPLIANCE_CALENDAR,
  filter: REDUCERS.APPLIANCE_ACTION,
});

export const onClickApplianceCalendarUpdateMonth = (event: Object) => ({
  type: ACTIONS.CHANGE_APPLIANCE_CALENDAR_MONTH,
  payload: event,
  filter: REDUCERS.APPLIANCE_ACTION,
});

export const onClickDeliveryDate = (event: Object) => ({
  type: ACTIONS.SELECT_APPLIANCE_DELIVERY_DATE,
  payload: event,
  filter: REDUCERS.APPLIANCE_ACTION,
});

export const fetchAvailableDeliverySlots = (
  depotDirect: Object = {}) => (
  dispatch: Function
) => {
  dispatch({
    type: ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS,
    filter: REDUCERS.APPLIANCE_ACTION,
  });
  const { originalDeliveryDate, currentDeliveryDate, zipCode } = depotDirect;
  const maxDeliveryDate = moment(originalDeliveryDate)
    .add(30, 'days')
    .format('YYYY-MM-DD');
  const payload: Object = availableDeliverySlotsRequest(zipCode, currentDeliveryDate, maxDeliveryDate);
  const { url, data } = payload;
  postRequest(url, data).then(
    (response: Object) => {
      setChangeDeliveryDateOverlayInfo();
      const { deliveryCalendarResponse } = response.data;
      // null check for bad responses
      const isSuccessful = deliveryCalendarResponse
        ? deliveryCalendarResponse.availableDeliverySlots
        : false;
      if (!isSuccessful) {
        errorCalendarResponse();
        dispatch({
          type: ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS_FAILURE,
          filter: REDUCERS.APPLIANCE_ACTION,
          message: response.data,
        });
      } else {
        const { availableDeliverySlots } = deliveryCalendarResponse;
        const mappedResponse = keyBy(availableDeliverySlots, (slot: Object) => {
          const { availableDate } = slot;
          const splitDate = availableDate.split('/');
          splitDate.unshift(splitDate.pop());
          const formatted = splitDate.join('-');
          return formatted;
        });
        dispatch({
          type: ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS_SUCCESS,
          filter: REDUCERS.APPLIANCE_ACTION,
          response: mappedResponse,
          payload: depotDirect,
          isCOMAppliance: false
        });
      }
    },
    (error: Object) => {
      errorCalendarResponse();
      dispatch({
        type: ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS_FAILURE,
        filter: REDUCERS.APPLIANCE_ACTION,
        message: error.response,
      });
    }
  );
};

export const fetchCOMAvailableDeliverySlots = (
  applianceDelivery: Object = {},
  orderId: string,
  emailId: string,
  fetchDateToken: string) => (
  dispatch: Function
) => {
  dispatch({
    type: ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS,
    filter: REDUCERS.APPLIANCE_ACTION,
  });
  const payload: Object = availableCOMApplianceDeliverySlotRequest(orderId, emailId, fetchDateToken);
  const { url, data } = payload;
  postRequest(url, data).then(
    (response: Object) => {
      setChangeDeliveryDateOverlayInfo();
      const {availableDeliverySlots, et} = response.data;
      const isSuccessful = !!availableDeliverySlots;
      if (!isSuccessful) {
        errorCalendarResponse();
        dispatch({
          type: ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS_FAILURE,
          filter: REDUCERS.APPLIANCE_ACTION,
          message: response.data,
        });
      } else {
        const mappedResponse = keyBy(availableDeliverySlots, (slot: Object) => {
          const { availableDate } = slot;
          const splitDate = availableDate.split('/');
          splitDate.unshift(splitDate.pop());
          const formatted = splitDate.join('-');
          return formatted;
        });
        dispatch({
          type: ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS_SUCCESS,
          filter: REDUCERS.APPLIANCE_ACTION,
          response: mappedResponse,
          payload: applianceDelivery,
          isCOMAppliance: true,
          rescheduleToken: et
        });
      }
    },
    (error: Object) => {
      errorCalendarResponse();
      dispatch({
        type: ACTIONS.FETCH_AVAILABLE_DELIVERY_SLOTS_FAILURE,
        filter: REDUCERS.APPLIANCE_ACTION,
        message: error.response,
      });
    }
  );
};

export const submitApplianceReschedule = (
  selectedDate: string,
  orderId: string
) => (dispatch: Function, getState: Function) => {
  const orderDetails = getActionData(
    getState(),
    REDUCERS.ORDER_DETAILS,
    orderId
  );
  const { emailId, depotDirect, custPhone } = orderDetails;
  const { customerOrderNo, storeNo } = depotDirect;

  dispatch({
    type: ACTIONS.SUBMIT_APPLIANCE_RESCHEDULE,
    filter: REDUCERS.APPLIANCE_ACTION,
  });

  const payload: Object = applianceRescheduleRequest(
    selectedDate,
    orderId,
    customerOrderNo,
    storeNo,
    emailId
  );

  const { url, data } = payload;
  postRequest(url, data).then(
    (response: Object) => {
      setApplianceDeliveryRescheduleInfo();
      if (response.data.status === 'fail') {
        applianceDeliveryRescheduleError();
        dispatch({
          type: ACTIONS.SUBMIT_APPLIANCE_RESCHEDULE_FAILURE,
          filter: REDUCERS.APPLIANCE_ACTION,
          message: response.data,
        });
      } else {
        setADRSuccessOverlayInfo();
        dispatch({
          type: ACTIONS.SUBMIT_APPLIANCE_RESCHEDULE_SUCCESS,
          filter: REDUCERS.APPLIANCE_ACTION,
          response: response.data,
        });
        getOrderDetails(orderId, emailId, custPhone)(dispatch);
      }
    },
    (error: Object) => {
      applianceDeliveryRescheduleError();
      dispatch({
        type: ACTIONS.SUBMIT_APPLIANCE_RESCHEDULE_FAILURE,
        filter: REDUCERS.APPLIANCE_ACTION,
        message: error.response,
      });
    }
  );
};

export const submitCOMApplianceReschedule = (
  orderId: string,
  selectedDate: string,
  rescheduleToken: string,
) => (dispatch: Function, getState: Function) => {
  const orderDetails = getActionData(
    getState(),
    REDUCERS.ORDER_DETAILS,
    orderId
  );
  const { emailId, custPhone } = orderDetails;

  dispatch({
    type: ACTIONS.SUBMIT_APPLIANCE_RESCHEDULE,
    filter: REDUCERS.APPLIANCE_ACTION,
  });

  const payload: Object = comApplianceRescheduleRequest(
    orderId,
    emailId,
    selectedDate,
    rescheduleToken
  );

  const { url, data } = payload;
  postRequest(url, data).then(
    (response: Object) => {
      setApplianceDeliveryRescheduleInfo();
      if (response.data.status === 'fail') {
        applianceDeliveryRescheduleError();
        dispatch({
          type: ACTIONS.SUBMIT_APPLIANCE_RESCHEDULE_FAILURE,
          filter: REDUCERS.APPLIANCE_ACTION,
          message: response.data,
        });
      } else {
        setADRSuccessOverlayInfo();
        dispatch({
          type: ACTIONS.SUBMIT_APPLIANCE_RESCHEDULE_SUCCESS,
          filter: REDUCERS.APPLIANCE_ACTION,
          response: response.data,
        });
        getOrderDetails(orderId, emailId, custPhone)(dispatch);
      }
    },
    (error: Object) => {
      applianceDeliveryRescheduleError();
      dispatch({
        type: ACTIONS.SUBMIT_APPLIANCE_RESCHEDULE_FAILURE,
        filter: REDUCERS.APPLIANCE_ACTION,
        message: error.response,
      });
    }
  );
};

/**
 * APPLIANCE CANCELLATION ACTIONS
 */

export const fetchApplianceCancelEligibility = (
  orderId: string,
  history: Object
) => (dispatch: Function, getState: Function) => {
  // get orderRefNumber
  const orderRefNumber = getOrderRefNumber(getState(), orderId);
  // get all appliance line items
  const lineItems = getOrderLineItems(getState(), orderId);
  const applianceItems = lineItems
    .filter(isEligibleApplianceItem)
    .map((item: Object) => {
      const { lineId, currentQuantity } = item;
      return { lineId, lineOrderedQty: currentQuantity };
    });

  // NOTE: below sorting is only for developement purpose
  applianceItems.sort((a: Object, b: Object) => {
    if (a.lineId > b.lineId) {
      return 1;
    }
    if (b.lineId > a.lineId) {
      return -1;
    }
    return 0;
  });

  // dispatch action for api request
  dispatch({
    type: ACTIONS.FETCH_MA_CANCEL_ELIGIBILITY,
    filter: REDUCERS.APPLIANCE_ACTION,
  });

  // create request payload
  const { url, data } = applianceCancelEligibilityRequest(
    orderId,
    orderRefNumber,
    applianceItems
  );

  // make api request
  postRequest(url, data).then(
    (response: Object) => {
      // deconstruct response object
      const { maCancelEligibilityResponse } = response.data;
      // check for empty eligible items
      const { lines } = maCancelEligibilityResponse;
      maCancelEligibilityResponse.isNoneEligible = false;
      // check if all lines are non-eligible
      if (!lines.line) {
        maCancelEligibilityResponse.lines =
          data.maCancelEligibilityRequest.lines;
        maCancelEligibilityResponse.isNoneEligible = true;
        // analytics: set no eligible item error
        setApplianceCancelErrorEvent(
          ERRORS.APPLIANCE_CANCEL_NO_LONGER_ELIGIBLE,
          'GEN_ERROR'
        );
      }
      // dispatch action for api success response
      dispatch({
        type: ACTIONS.FETCH_MA_CANCEL_ELIGIBILITY_SUCCESS,
        filter: REDUCERS.APPLIANCE_ACTION,
        response: maCancelEligibilityResponse,
        lineItems: keyBy(lineItems.filter(isEligibleApplianceItem), 'lineId'),
      });
      // set analytics page
      setApplianceCancelPageInfo();
      // route to request cancellation page
      history.push(ROUTES.PATH.GUEST_MA_CANCELLATION);
    },
    (error: Object) => {
      // dispatch action for api failure response
      const { response } = error;
      dispatch({
        type: ACTIONS.FETCH_MA_CANCEL_ELIGIBILITY_FAILURE,
        filter: REDUCERS.APPLIANCE_ACTION,
        message: response,
      });
      // analytics: appliance cancel eligibility error
      setApplianceCancelErrorEvent(
        `${ERRORS.HEADER} ${ERRORS.DETAILS_CONTENT}`,
        getErrorCode(response)
      );
    }
  );
};

export const requestApplianceCancellation = (
  eligibleLines: Object,
  lineItems: Object
) => (dispatch: Function, getState: Function) => {
  // dispatch action for api request
  dispatch({
    type: ACTIONS.MA_CANCEL_REQUEST,
    filter: REDUCERS.APPLIANCE_ACTION,
  });

  // retrieve all necessary values
  const { orderId, orderRefNumber, lines } = eligibleLines;
  const { depotDirect, versionId } = getActionData(
    getState(),
    REDUCERS.ORDER_DETAILS,
    orderId
  );

  const { line } = lines;
  const eligibleItems = line.map((item: Object) => {
    const { lineId } = item;
    const currentItem = lineItems[lineId];
    const { currentQuantity, storeSKU } = currentItem;
    const applianceShippingAndHandling = currentItem.fulfillment.applianceShippingAndHandling ? currentItem.fulfillment.applianceShippingAndHandling : {};
    const storeNo = applianceShippingAndHandling.applianceDeliveries[0].storeId || '';
    const workOrderNo = applianceShippingAndHandling.applianceDeliveries[0].workOrderNo || '';
    return {
      lineReference: lineId,
      quantityToCancel: currentQuantity,
      skuCode: storeSKU,
      storeNo,
      workOrderNumber: workOrderNo
    };
  });
  const storeNo = depotDirect ? depotDirect.storeNo : eligibleItems[0].storeNo;
  // create request payload
  const { url, data } = applianceCancelRequest(
    orderId,
    orderRefNumber,
    storeNo,
    versionId,
    eligibleItems
  );

  // make api request
  postRequest(url, data).then(
    (response: Object) => {
      // dispatch action for api success response
      dispatch({
        type: ACTIONS.MA_CANCEL_REQUEST_SUCCESS,
        filter: REDUCERS.APPLIANCE_ACTION,
        response: response.data,
      });
      // analytics: appliance cancel request success
      setApplianceCancelSuccess(
        eligibleItems,
        getMappedData(getState(), REDUCERS.APPLIANCE_ACTION),
        getActionData(getState(), REDUCERS.ORDER_DETAILS, orderId)
      );
      scrollToTop();
    },
    (error: Object) => {
      const { response } = error;
      // dispatch action for api failure response
      dispatch({
        type: ACTIONS.MA_CANCEL_REQUEST_FAILURE,
        filter: REDUCERS.APPLIANCE_ACTION,
        message: response,
      });
      // analytics: appliance cancel request error
      setApplianceCancelErrorEvent(
        `${ERRORS.HEADER} ${ERRORS.CANCEL_GENERAL}`,
        getErrorCode(response)
      );
      scrollToTop();
    }
  );
};

export const resetApplianceActionError = () => (dispatch: Function) => {
  resetError(dispatch, REDUCERS.APPLIANCE_ACTION);
};
